export type ComparisonLang = 'en' | 'tr'
export type CompareCell = boolean | 'partial' | string
export interface ComparisonSource { label: string; href: string }
export interface ComparisonColumn { name: string; highlight?: boolean }
export interface ComparisonEntry {
  id?: string
  feature: string
  detail?: string
  values: CompareCell[]
  sources?: ComparisonSource[]
}

export function cellText(value: CompareCell, lang: ComparisonLang): string {
  if (value === true) return lang === 'tr' ? 'Destekleniyor' : 'Supported'
  if (value === false) return lang === 'tr' ? 'Sunulmuyor' : 'Not offered'
  if (value === 'partial') return lang === 'tr' ? 'Kısmi; kapsamı doğrulayın' : 'Partial; verify scope'
  return value
}

export function comparisonScope(lang: ComparisonLang): string {
  return lang === 'tr'
    ? 'Ürün kapsamını karşılaştırın. “Destekleniyor”, özelliğin başlangıç fiyatına dahil olduğu anlamına gelmez. Paket, ek ürün ve kurulum koşullarını hücrelerdeki açıklamalarla birlikte değerlendirin.'
    : 'Compare product coverage. “Supported” does not mean included in the entry price. Read each value alongside its plan, additional-product, and deployment conditions.'
}

/** Deterministic fragments work in static HTML, JSON-LD, and both viewport layouts. */
export function comparisonSlug(label: string): string {
  return label.toLowerCase().replace(/ı/g, 'i').normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'item'
}

export function identifyEntries(rows: ComparisonEntry[]) {
  const seen = new Set<string>()
  return rows.map(row => {
    const base = comparisonSlug(row.id || row.feature)
    let id = `feature-${base}`
    let suffix = 2
    while (seen.has(id)) id = `feature-${base}-${suffix++}`
    seen.add(id)
    return { ...row, id }
  })
}

export function comparisonSchema({ url, title, description, caption, lang, columns, rows }: {
  url: string; title: string; description: string; caption: string; lang: ComparisonLang
  columns: ComparisonColumn[]; rows: ComparisonEntry[]
}) {
  return {
    '@type': 'WebPage',
    '@id': `${url}#webpage`,
    url, name: title, description, inLanguage: lang,
    publisher: { '@type': 'Organization', name: 'Monofor', url: 'https://monofor.com' },
    about: columns.map(column => ({ '@type': 'Thing', name: column.name })),
    mainEntity: {
      '@type': 'ItemList', '@id': `${url}#comparison`, name: caption,
      description: comparisonScope(lang),
      itemListOrder: 'https://schema.org/ItemListUnordered', numberOfItems: rows.length,
      itemListElement: identifyEntries(rows).map((row, index) => ({
        '@type': 'ListItem', position: index + 1, name: row.feature,
        url: `${url}#${row.id}`,
        description: [row.detail, ...columns.map((column, i) => `${column.name}: ${cellText(row.values[i], lang)}.`)].filter(Boolean).join(' '),
      })),
    },
  }
}
